'use client';

import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * **Enter / glide / exit for a list whose rows come and go** (LF-6186,
 * promoted to production by LF-6304 / [ATTR-3]).
 *
 * Lifted out of `attribute-field.tsx`, where it was ~150 lines tangled into the
 * attribute grid. Nothing in here knows what an attribute is: it takes a list
 * of items and a way to key them, and returns the list that should actually
 * render plus the refs to measure it with. Any list or grid whose rows are
 * gated on data — a filtered table, a conditional form, a stepped wizard — gets
 * the same motion by calling this.
 *
 * The motion vocabulary is a **parameter**, not a constant, so the hook carries
 * no design tokens of its own; the caller passes the one its surface uses (the
 * attribute grid passes `ATTRIBUTE_MOTION`). It lives beside `attribute-tokens.ts`
 * in `components/attributes/`, where LF-6388 / [ATTR-10] moved the shared form
 * system out of `components/leads/`.
 *
 * ## What it does
 *
 * Three distinct things happen when a gate flips, and each gets its own
 * treatment:
 *
 * · **A row that appears** fades and drops the last few pixels into place.
 * · **A row that makes way for it** glides.
 * · **A row that is removed** plays the entrance in reverse: it fades and
 *   lifts, while the rows below glide up to close the gap *at the same time*,
 *   not after it.
 *
 * The exit is the one that needs machinery. React unmounts a removed row
 * immediately, so there is nothing left to animate — the row has to be held on
 * screen deliberately. {@link RowMotion.rows} is the rendered list and it keeps
 * a departing key for one beat longer than `items` does.
 *
 * The departing row is then taken **out of flow** (`position: absolute` at the
 * coordinates it last occupied) before the glide is measured. That is what lets
 * the two happen together: the moment it stops taking space, the rows below
 * have somewhere to go, and the FLIP pass in the same frame carries them there.
 *
 * ## One vocabulary: everything glides, along one axis
 *
 * Every row that moves glides — but on the **dominant axis only**. A row that
 * swaps column on the same line slides sideways; a row that moves to a
 * different line slides vertically and lets its column change land immediately.
 * {@link resolveGlide} is that rule, on its own, in one pure function.
 *
 * Animating both axes is what sent half the following rows diagonally across
 * the form at once, which read as the layout being shuffled rather than
 * adjusting. An opacity dip was tried instead and is also wrong: it makes those
 * rows behave unlike every other row. One axis keeps every row on the same
 * motion without ever drawing a diagonal.
 *
 * ## Only on a real change to the row list — and never on mount
 *
 * The pass used to run on every render, which replayed a movement animation
 * across the whole sheet on the first click of any field. Nothing had moved:
 * the baseline was captured on mount while the drawer was still sliding in, and
 * was never refreshed because nothing re-rendered until that click. Guarding on
 * the row list fixes it, and measuring **relative to the container** rather
 * than the viewport makes the whole pass immune to ancestor motion and to
 * scrolling.
 *
 * **The very first pass records the baseline and animates nothing** (LF-6304).
 * Rows present at mount have not *arrived* — no gate let them in — so playing
 * the entrance on them would animate the surface merely opening. It also breaks
 * the promotion's contract: the enquiry drawer has no gated rows at all and
 * must stay motion-free in practice, and without this every one of its rows
 * would fade in each time the drawer opened.
 *
 * All of it is skipped under `prefers-reduced-motion`.
 *
 * ## What the caller has to do
 *
 * Three things, all of them load-bearing:
 *
 * 1. Render {@link RowMotion.rows}, not its own list — that is what keeps a
 *    departing row on screen.
 * 2. Put {@link RowMotion.containerRef} on a **`position: relative`** element.
 *    A departing row is placed at container-relative coordinates; without a
 *    positioned ancestor it resolves against the nearest one that is — for the
 *    attribute grid that was the `fixed` sheet, and a row leaving at grid-y
 *    89px was drawn 89px from the top of the *drawer*, above the first section
 *    heading.
 * 3. Give every row `ref={registerRow(key)}` with the same key `keyOf`
 *    produces, so the pass can find the node to measure.
 */

/**
 * The durations, curves and distances the pass animates with.
 *
 * Structural, so a caller's own token object satisfies it without importing
 * anything from here. `attribute-tokens.ts` declares `satisfies RowMotionSpec`
 * against it, which is what stops the two drifting.
 */
export interface RowMotionSpec {
  /** A row arriving: fade and settle the last few pixels. */
  enterMs: number;
  /** A row leaving. Should be shorter than the entrance so it clears the spot first. */
  exitMs: number;
  /** A row making way for another, or closing the gap one left. */
  glideMs: number;
  /** How far a row lifts as it fades out, and drops in as it arrives. */
  offsetPx: number;
  /** The furthest any row is asked to travel, whatever the real distance. */
  maxTravelPx: number;
  /** Decelerating. Used for arrivals and glides. */
  ease: string;
  /** Front-loaded, so most of an exit's fade happens in its first third. */
  exitEase: string;
}

/** One entry in the rendered list. */
export interface MotionRow<TItem> {
  item: TItem;
  /** What `keyOf` returned for it — the same key {@link RowMotion.registerRow} wants. */
  key: string;
  /**
   * True while the row is on its way out. It is inert: absolutely positioned,
   * `pointer-events: none`, and about to unmount. Callers that step through
   * rows with the keyboard have to skip it, and it should be `aria-hidden`.
   */
  leaving: boolean;
}

export interface RowMotion<TItem> {
  /** Goes on the `position: relative` element that contains the rows. */
  containerRef: RefObject<HTMLDivElement | null>;
  /** `ref={registerRow(key)}` on each row, keyed exactly as `keyOf` keys it. */
  registerRow: (key: string) => (node: HTMLDivElement | null) => void;
  /**
   * What to render: the live rows **first, in `items` order**, then any row
   * still on its way out.
   *
   * That ordering is part of the contract rather than an implementation
   * detail: it means index `i` of this list is index `i` of `items` for every
   * live row, so a caller that already indexes into its own list — the shared
   * grid's keyboard chain does, through `useAttributeCommit` — can render
   * `rows` without re-mapping anything, and can never open an editor on a
   * departing row because those indices sit past the end of `items`.
   */
  rows: MotionRow<TItem>[];
}

/** Whether the reader has asked the interface to stop moving. */
export function prefersReducedMotion(): boolean {
  return Boolean(
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
  );
}

/**
 * **One axis, never both — and never further than the cap.** The two motion
 * rules that are pure arithmetic, extracted so they can be asserted directly
 * rather than inferred from a rendered frame.
 *
 * · **The axis is the dominant one.** A row that swaps column *on the same
 *   line* (`dy` under a pixel) slides sideways; anything else slides
 *   vertically and lets its column change land immediately. Animating both is
 *   what sent half the following rows diagonally across the form at once,
 *   which read as the layout being shuffled rather than adjusting.
 * · **Travel is clamped to `maxTravelPx`, sign preserved.** A vertical move is
 *   one row (~30px) and glides in full; a same-line column swap is really
 *   ~141px — half the form — and animating the true distance reads as a field
 *   flying across the drawer. A long move starts at the cap instead and the
 *   remainder is skipped in the frame the layout changes, so there is nothing
 *   to see.
 *
 * `null` means *do not animate this row*: it moved by less than a pixel on
 * both axes, which is sub-pixel layout noise rather than a move.
 *
 * @param dx how far the row has moved horizontally — **previous minus next**,
 *   i.e. the offset it must start from to appear not to have moved yet.
 * @param dy the same, vertically.
 */
export function resolveGlide({
  dx,
  dy,
  maxTravelPx
}: {
  dx: number;
  dy: number;
  maxTravelPx: number;
}): { axis: 'X' | 'Y'; distance: number } | null {
  if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return null;

  const axis = Math.abs(dy) < 1 ? 'X' : 'Y';
  const travelled = axis === 'X' ? dx : dy;
  return {
    axis,
    distance: Math.sign(travelled) * Math.min(Math.abs(travelled), maxTravelPx)
  };
}

export function useRowMotion<TItem>({
  items,
  keyOf,
  motion
}: {
  items: TItem[];
  keyOf: (item: TItem) => string;
  motion: RowMotionSpec;
}): RowMotion<TItem> {
  const containerRef = useRef<HTMLDivElement>(null);
  const rowNodesRef = useRef(new Map<string, HTMLDivElement>());
  const prevRectsRef = useRef(new Map<string, { x: number; y: number }>());
  const prevKeysRef = useRef<string[] | null>(null);
  const exitTimerRef = useRef<number | null>(null);

  /**
   * Keys the list is still holding on screen — the live ones, plus any that
   * `items` has dropped but whose exit has not finished.
   *
   * Settled back to the live list once an exit completes, which is what finally
   * unmounts a departed row. Holding it here (rather than gating the animation
   * on `items`) is what lets an arrival and a departure both be caught in a
   * **single** pass: an arriving row renders immediately, and a departing one
   * is still in the DOM when the effect goes looking for it.
   */
  const [heldKeys, setHeldKeys] = useState<string[]>(() => items.map(keyOf));
  /**
   * Last-known item per key, so a departing row still has something to render
   * while it fades. State rather than a ref because the render reads it, and
   * reading a ref during render is exactly the hazard `react-hooks/refs` exists
   * to catch — a ref write would not schedule the re-render this depends on.
   *
   * Merged, never pruned on update: the whole point is that a key which has
   * just left `items` keeps its item until the exit finishes.
   */
  const [itemSnapshot, setItemSnapshot] = useState<Map<string, TItem>>(
    () => new Map(items.map((item) => [keyOf(item), item]))
  );

  /**
   * What actually renders: the live rows, then any row still on its way out.
   *
   * A departing row is **appended**, not left where it was — see
   * {@link RowMotion.rows} for why that ordering is load-bearing. It is
   * absolutely positioned by the effect, so where it sits in the list has no
   * visual consequence.
   */
  const liveKeys = items.map(keyOf);
  const leavingRows = heldKeys
    .filter((key) => !liveKeys.includes(key))
    .map((key) => ({ key, item: itemSnapshot.get(key) }))
    .filter(
      (row): row is { key: string; item: TItem } => row.item !== undefined
    )
    .map(({ key, item }) => ({ item, key, leaving: true }));

  const rows: MotionRow<TItem>[] = [
    ...items.map((item) => ({ item, key: keyOf(item), leaving: false })),
    ...leavingRows
  ];

  /*
    **The four suppressions in this effect, and why they are not laziness**
    (LF-6304). They are new only in the sense that the file moved: under
    `components/prototypes/**` the config relaxes `exhaustive-deps` and
    `set-state-in-effect` wholesale, so promoting the hook is what first put it
    under the production rules. Nothing about the code changed.

    · **No dependency array, deliberately.** This is a FLIP pass: it has to run
      after *every* render, because the only render it can measure a departing
      row in is the one that still contains it. The guard is `listChanged`
      below, on the rendered key list — a real, cheap check that the pass is
      warranted, rather than a dependency array standing in for one. Listing
      the twelve dependencies the rule asks for would change nothing (`rows`,
      `liveKeys`, `items` and `keyOf` are all rebuilt every render, so the
      effect would still run every time) while implying a memoisation contract
      the hook does not have.

    · **Three `setState` calls inside it.** Holding a removed row on screen so
      it can be animated out is state that can only be written *after* layout —
      React has already decided to unmount it, and the measurement that decides
      where to place it does not exist until the DOM does. "You might not need
      an effect" is about deriving state that could be computed during render;
      none of these can be. The alternative is writing a ref during render,
      which is the hazard the neighbouring comment on `itemSnapshot` explains
      and which would not schedule the re-render this depends on.

    Both rules are duplicated across the `react-hooks/*` and `@eslint-react/*`
    plugins, so each site names both.
  */
  // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
  useLayoutEffect(() => {
    /*
      Driven by what is *rendered*, not by `items`: a departing row is still
      rendered at this point, which is the only moment it can be animated.

      The key carries the `leaving` flag, and that is not decoration. A row
      held for its exit keeps its key in the rendered list, so for the **last**
      row in a section the list is byte-identical before and after it departs —
      the guard saw no change, never ran the exit, and never started the
      unmount timer, so the attribute grid's `Verification note` simply stayed
      on screen forever when `Verified` went to No. `Date of death` masked the
      bug by being mid-list: holding it moved it to the end, which did change
      the order.
    */
    // NUL as the separator: it cannot occur in a caller's key, so a key can
    // never collide with some other key's "leaving" form.
    const renderedKeys = rows.map(
      (row) => `${row.key}${row.leaving ? '\u0000leaving' : ''}`
    );
    const previous = prevKeysRef.current;
    const listChanged =
      !previous ||
      previous.length !== renderedKeys.length ||
      previous.some((key, index) => key !== renderedKeys[index]);
    if (!listChanged) return;
    prevKeysRef.current = renderedKeys;

    const reduced = prefersReducedMotion();

    /** Row origins relative to the container, so ancestor motion cancels out. */
    const positions = () => {
      const origin = containerRef.current?.getBoundingClientRect();
      const map = new Map<string, { x: number; y: number }>();
      if (!origin) return map;
      for (const [key, node] of Array.from(rowNodesRef.current)) {
        const rect = node.getBoundingClientRect();
        map.set(key, {
          x: rect.left - origin.left,
          y: rect.top - origin.top
        });
      }
      return map;
    };

    /*
      Mount: record where everything started and animate nothing. Rows on
      screen at mount have not arrived — no gate let them in — so entering
      them animates the surface merely opening, which is what would have made
      the enquiry drawer (no gated rows at all) fade in every row on every
      open. See the hook's header.
    */
    if (previous === null) {
      prevRectsRef.current = positions();
      return;
    }

    const departing = rows.filter((row) => row.leaving).map((row) => row.key);
    const arriving = liveKeys.filter((key) => !previous.includes(key));

    if (reduced) {
      prevRectsRef.current = positions();
      // Drop the departing row immediately — see the suppression note above.
      // eslint-disable-next-line @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect
      if (departing.length) setHeldKeys(liveKeys);
      return;
    }

    // 1. Lift every departing row out of flow, at the spot it was occupying,
    //    and start it fading. Doing this *before* the glide is measured is
    //    what lets the gap close at the same time rather than afterwards.
    for (const key of departing) {
      const node = rowNodesRef.current.get(key);
      const at = prevRectsRef.current.get(key);
      if (!node || !at) continue;
      node.style.position = 'absolute';
      node.style.left = `${at.x}px`;
      node.style.top = `${at.y}px`;
      node.style.width = `${node.getBoundingClientRect().width}px`;
      node.style.pointerEvents = 'none';
      node.style.transition = `opacity ${motion.exitMs}ms ${motion.exitEase}, transform ${motion.exitMs}ms ${motion.exitEase}`;
      node.style.opacity = '0';
      node.style.transform = `translateY(-${motion.offsetPx}px)`;
    }

    // 1.5 A key can come BACK while its exit is still on screen — a gate
    //     flipping twice inside `exitMs`, or a host whose two state writes land
    //     a render apart. React reuses the DOM node (same key), so the exit's
    //     out-of-flow styles are still on it, and nothing else here resets
    //     them: the entrance below touches only opacity and transform, which
    //     left the row absolutely positioned at stale coordinates, opaque,
    //     painted over whichever row now owns that spot, with `pointer-events:
    //     none` making its control dead (VALE-1375). Clear the residue BEFORE
    //     measuring, so step 2 sees the row back in flow at its real place.
    for (const key of arriving) {
      const node = rowNodesRef.current.get(key);
      if (!node || node.style.position !== 'absolute') continue;
      node.style.transition = 'none';
      node.style.position = '';
      node.style.left = '';
      node.style.top = '';
      node.style.width = '';
      node.style.pointerEvents = '';
      node.style.opacity = '';
      node.style.transform = '';
    }

    // 2. Reading positions here forces the layout the step above caused, so
    //    these are the post-removal coordinates.
    const now = positions();

    for (const [key, node] of Array.from(rowNodesRef.current)) {
      if (departing.includes(key)) continue;
      const next = now.get(key);

      // 3. An arriving row has no previous position — it plays the entrance.
      if (arriving.includes(key)) {
        node.style.transition = 'none';
        node.style.opacity = '0';
        node.style.transform = `translateY(-${motion.offsetPx}px)`;
        requestAnimationFrame(() => {
          node.style.transition = `opacity ${motion.enterMs}ms ease-out, transform ${motion.enterMs}ms ${motion.ease}`;
          node.style.opacity = '';
          node.style.transform = '';
        });
        continue;
      }

      const prev = prevRectsRef.current.get(key);
      if (!prev || !next) continue;

      // 4. Glide into the space that just opened or closed — one axis, capped.
      const glide = resolveGlide({
        dx: prev.x - next.x,
        dy: prev.y - next.y,
        maxTravelPx: motion.maxTravelPx
      });
      if (!glide) continue;

      node.style.transition = 'none';
      node.style.transform = `translate${glide.axis}(${glide.distance}px)`;
      requestAnimationFrame(() => {
        node.style.transition = `transform ${motion.glideMs}ms ${motion.ease}`;
        node.style.transform = '';
      });
    }

    prevRectsRef.current = now;

    // Keep a departing row's last-known item so it has something to render
    // while it fades — see the suppression note above.
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setItemSnapshot((previousSnapshot) => {
      const merged = new Map(previousSnapshot);
      for (const item of items) merged.set(keyOf(item), item);
      return merged;
    });

    // 5. Unmount the departing rows once their exit has played.
    if (departing.length) {
      if (exitTimerRef.current) window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = window.setTimeout(
        () => setHeldKeys(liveKeys),
        motion.exitMs
      );
    } else if (
      heldKeys.length !== liveKeys.length ||
      heldKeys.some((key, index) => key !== liveKeys[index])
    ) {
      // Re-sync after a reorder that left nothing behind — see the note above.
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setHeldKeys(liveKeys);
    }
  });

  const registerRow = (key: string) => (node: HTMLDivElement | null) => {
    if (node) rowNodesRef.current.set(key, node);
    else rowNodesRef.current.delete(key);
  };

  return { containerRef, registerRow, rows };
}
